import dayjs from "dayjs";
import { LEAVE_KIND_LABEL, COMPENSATION_LABEL } from "./create/fields/codeLabels";

/**
 * 양식 정의 한 칸 + 저장된 값 → 상세 화면에 찍을 문자열.
 *
 * <h3>왜 필요한가</h3>
 * 상세는 «값»(formData) 과 «정의»(그 문서에 박힌 버전의 fields) 를 따로 받는다. 값은 입력 UI 가
 * 만든 모양 그대로라 사람이 읽을 형태가 아니다 — 거래처는 {@code {id,name}}, 반복표는 객체 배열,
 * 휴가 종류는 {@code ANNUAL} 같은 <b>코드</b>다. 코드를 그대로 저장하는 건 관리자가 라벨을 바꿔도
 * 연차 차감이 어긋나지 않게 하려는 의도적 선택이므로(codeLabels 참고), 사람이 읽을 변환은
 * 화면이 맡는다.
 *
 * <p>상신 화면({@code FieldRenderer})이 «타입 → 입력 UI» 를 아는 것과 짝이 되는 «타입 → 표시 문자열»
 * 이다. 두 곳이 같은 타입 목록을 보므로, 관리자가 새 타입을 쓰면 양쪽 모두 기본 분기로 흘러
 * 화면이 깨지지 않는다.
 */
export function fieldValueText(field, raw) {
    if (raw === null || raw === undefined || raw === "") return "";

    const type = field?.type;

    // 반복표 — 한 행을 «열이름 값 · 열이름 값» 한 줄로 편다. 상세는 편집이 없으니 표를 다시
    // 그리기보다 줄글이 읽기 쉽고, 열이 늘어도 가로로 터지지 않는다.
    if (type === "GRID" || Array.isArray(raw)) {
        const arr = Array.isArray(raw) ? raw : [];
        if (arr.length === 0) return "";
        const cols = field?.columns || [];
        return arr
            .map((row) => {
                if (row === null || typeof row !== "object") return codeLabel(row);
                if (cols.length === 0) return objectText(row);
                return cols
                    .map((c) => {
                        const cell = fieldValueText(c, row[c.key]);
                        return cell ? `${c.label} ${cell}` : null;
                    })
                    .filter(Boolean)
                    .join(" · ");
            })
            .filter(Boolean)
            .join("\n");
    }

    // 참조형(거래처·직원·채널·근거 결재)은 {id, name|title} 로 저장된다. id 는 화면에 쓸모가 없다.
    if (typeof raw === "object") return objectText(raw);

    if (typeof raw === "boolean") return raw ? "예" : "아니오";

    switch (type) {
        case "MONEY": {
            const n = Number(String(raw).replace(/,/g, ""));
            return Number.isFinite(n) ? `${n.toLocaleString("ko-KR")} 원` : String(raw);
        }
        case "DATE": {
            const d = dayjs(raw);
            return d.isValid() ? d.format("YYYY.MM.DD") : String(raw);
        }
        default:
            return codeLabel(raw);
    }
}

/** 값칸을 세로로 넉넉하게 그릴 칸 — 긴 글과 여러 줄로 펴지는 반복표. */
export function isLongField(field) {
    return field?.type === "TEXTAREA" || field?.type === "GRID";
}

/** 옛 문서의 본문을 담는 칸에 붙는 이름. 양식(GEN_WORK_REPORT)이 쓰는 라벨과 같게 맞춘다. */
export const LEGACY_BODY_LABEL = "본문 (이전 양식으로 작성된 건)";

/**
 * 상세에 <b>실제로 그릴</b> 칸만 고른다 — 값이 없는 칸은 행 자체를 만들지 않는다.
 *
 * <h3>왜 빈 칸을 지우는가</h3>
 * 상세는 읽기 전용이라 빈 칸은 알려주는 게 없다. 그런데 양식이 커지면서(대금지급요청 10칸)
 * 안 쓴 칸이 «—» 로 줄줄이 남아 정작 채워진 값이 묻힌다. 특히 옛 문서를 위해 남겨 둔
 * «본문 (이전 양식으로 작성된 건)» 같은 칸은 신규 문서에서는 항상 비어 있어, 새 칸과 나란히
 * 빈 행으로 겹쳐 보인다. 채워진 것만 남기면 문서가 실제로 담고 있는 내용과 화면이 같아진다.
 *
 * @param visible 조건부 표시(showIf)를 이미 통과한 칸들
 */
export function filledFields(visible, values) {
    const v = values || {};
    return (visible || []).filter((f) => fieldValueText(f, v[f.key]).trim() !== "");
}

/**
 * 옛 양식으로 올라간 문서의 본문 — 지금 양식에는 담을 칸이 없어 승격 컬럼에만 남아 있는 글.
 *
 * <h3>왜 따로 꺼내는가</h3>
 * 개편 전 문서는 백필로 양식이 배정됐지만, 새 양식의 칸과 옛 문서의 칸이 1:1 이 아니다.
 * 슬롯이 붙은 값(금액·지급예정일·거래처)은 서버가 되채워 주는데, 슬롯이 없는 «지급 내용»·
 * «구매 사유» 같은 본문은 채울 칸 자체가 없어 통째로 사라진다 — 결재자가 판단할 근거가
 * 그 글인데도 그렇다. 옛 유형별 DTO({@code form})에는 그대로 남아 있으므로 거기서 꺼낸다.
 *
 * <p>이미 어느 칸에 같은 글이 찍혔으면 {@code null} 이다. 안 그러면 같은 문장이 두 번 나온다.
 */
export function legacyBodyText(type, form, shownTexts = []) {
    const d = form || {};
    const body = (() => {
        switch (type) {
            case "PAYMENT":       return d.description;
            case "PURCHASE":      return d.reason;
            case "BUSINESS_TRIP": return d.purpose;
            case "VACATION":
            case "OVERTIME":
            case "HOLIDAY_WORK":  return d.reason;
            default:              return d.content;
        }
    })();

    const norm = (s) => String(s ?? "").replace(/\s+/g, " ").trim();
    const text = norm(body);
    if (!text) return null;
    return shownTexts.some((t) => norm(t) === text) ? null : String(body);
}

/** 참조형 값 → 사람이 읽는 이름. id 는 버리고 name/title 을 쓴다. */
function objectText(o) {
    if (o === null || o === undefined) return "";
    if (typeof o !== "object") return codeLabel(o);
    if (Array.isArray(o)) return o.map(objectText).filter(Boolean).join(", ");
    const named = o.name ?? o.title ?? o.label;
    if (named !== undefined && named !== null && named !== "") return String(named);
    // 모르는 모양이면 값만 이어 붙인다. JSON 을 그대로 보여주면 사람이 못 읽는다.
    return Object.entries(o)
        .filter(([k, v]) => k !== "id" && v !== null && v !== undefined && v !== "")
        .map(([, v]) => codeLabel(v))
        .join(" · ");
}

/** 코드로 저장된 값(ANNUAL·COMP_DAY…)만 한글로 바꾼다. 모르는 값은 손대지 않는다. */
function codeLabel(v) {
    return LEAVE_KIND_LABEL[v] ?? COMPENSATION_LABEL[v] ?? String(v);
}
